import React, { useState } from "react";
import { BrainCircuit, Check, RefreshCw, X } from "lucide-react";

import {
  confirmAgentMemory,
  confirmAgentMemoryUpdate,
  deactivateAgentMemory,
} from "../services/agentServices";

import "../sass/components/agent-memory-suggestion.scss";

export default function AgentMemorySuggestion({
  token,
  suggestion,
  onResolved,
}) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  if (!suggestion) return null;

  const isUpdate = suggestion.action === "UPDATE";
  const memoryId = suggestion.existingMemoryId || suggestion.id;
  const loading = status === "loading";

  const handleConfirm = async () => {
    if (!memoryId || loading) return;

    setStatus("loading");
    setError("");

    try {
      const response = isUpdate
        ? await confirmAgentMemoryUpdate({
            token,
            memoryId,
            suggestedContent: suggestion.suggestedContent,
            suggestedSummary: suggestion.suggestedSummary,
            relevancia: suggestion.relevancia,
          })
        : await confirmAgentMemory({
            token,
            memoryId,
          });

      setStatus("success");
      onResolved?.({
        action: isUpdate ? "UPDATED" : "CONFIRMED",
        suggestion,
        response,
      });
    } catch (requestError) {
      setStatus("error");
      setError(
        requestError?.message || "Não foi possível confirmar a memória."
      );
    }
  };

  const handleIgnore = async () => {
    if (loading) return;

    setStatus("loading");
    setError("");

    try {
      if (!isUpdate && memoryId) {
        await deactivateAgentMemory({ token, memoryId });
      }

      setStatus("ignored");
      onResolved?.({ action: "IGNORED", suggestion });
    } catch (requestError) {
      setStatus("error");
      setError(requestError?.message || "Não foi possível ignorar a memória.");
    }
  };

  if (status === "success" || status === "ignored") return null;

  return (
    <section className="psya-memory-card">
      <div className="psya-memory-card__icon" aria-hidden="true">
        <BrainCircuit size={18} />
      </div>

      <div className="psya-memory-card__body">
        <div className="psya-memory-card__header">
          <span className="psya-memory-card__badge">Memória da PSYA</span>
          <h3>
            {isUpdate
              ? "Posso atualizar esta informação?"
              : "Posso lembrar desta informação?"}
          </h3>
          <p>
            {isUpdate
              ? "Você controla o que a PSYA mantém como referência para conversas futuras."
              : "Essa informação só entra na memória depois da sua confirmação."}
          </p>
        </div>

        {isUpdate ? (
          <div className="psya-memory-card__comparison">
            <div>
              <span>Informação atual</span>
              <p>{suggestion.previousSummary || suggestion.previousContent}</p>
            </div>

            <div className="psya-memory-card__comparison-new">
              <span>Nova informação</span>
              <p>{suggestion.suggestedSummary || suggestion.suggestedContent}</p>
            </div>
          </div>
        ) : (
          <p className="psya-memory-card__content">
            {suggestion.resumo || suggestion.conteudo}
          </p>
        )}

        {error ? <p className="psya-memory-card__error">{error}</p> : null}

        <div className="psya-memory-card__actions">
          <button
            type="button"
            className="psya-memory-card__confirm"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <RefreshCw size={15} className="psya-memory-card__spinner" />
            ) : (
              <Check size={15} />
            )}
            {loading
              ? "Processando..."
              : isUpdate
                ? "Atualizar memória"
                : "Guardar memória"}
          </button>

          <button
            type="button"
            className="psya-memory-card__ignore"
            onClick={handleIgnore}
            disabled={loading}
          >
            <X size={15} />
            {isUpdate ? "Manter atual" : "Agora não"}
          </button>
        </div>
      </div>
    </section>
  );
}
